import * as React from 'react'

import {
  AddToBasketButton,
  Bar,
  BarWrapper,
  BundlePrice,
  BundlePriceText,
  BundlePriceWrapper,
  ButtonContainer,
  CircularCurrentIndicator,
  CircularItemsText,
  CircularProgressContainer,
  CircularTextWrapper,
  CircularWrapper,
  ProgressBarContainer,
  ProgressBarTotalContentText,
  StyledCircularProgress,
  StyledHiddenCircularProgress,
  StyledProgressBar,
  StyledTotalSummary,
} from './styles'
import {
  CircularIndicatorType,
  ProgressIndicatorStyle,
  ProgressIndicatorType,
} from './theme'

const MAX_QUANTITY_SELECTION = 200

export type ProgressIndicatorProps = {
  type: ProgressIndicatorType
  currentIndicator: number
  maxItems: number
  unlimitedProducts?: boolean
  totalPrice?: string
  bundleSummaryAmount?: string
  i18nText?: {
    totalText?: string
    itemsText?: string
    itemsAlternateText?: string
    addToBagText?: string
  }
  handleAddToBasketCallback?: () => void
  style: ProgressIndicatorStyle
  disabledAddToBasket?: boolean
}

const shouldDisplayAddToBasket = (
  handleAddToBasketCallback: (() => void) | undefined,
  currentIndicator: number,
  maxItems: number,
  unlimitedProducts: boolean = false,
) =>
  handleAddToBasketCallback &&
  (currentIndicator === maxItems ||
    ((unlimitedProducts || !maxItems) && currentIndicator > 0))

const BarIndicator = (props: ProgressIndicatorProps) => {
  const maxProgressBarValue: number = 100
  const displayAddToBasket = shouldDisplayAddToBasket(
    props.handleAddToBasketCallback,
    props.currentIndicator,
    props.maxItems,
    props.unlimitedProducts,
  )

  const progressIndicatorText =
    props.i18nText?.itemsText &&
    props.i18nText?.itemsAlternateText &&
    `${props.currentIndicator} ${
      props.unlimitedProducts || !props.maxItems
        ? props.i18nText.itemsText
        : props.i18nText.itemsAlternateText
    }`

  return (
    <BarWrapper
      position={props.style.barIndicator.position}
      padding={props.style.barIndicator.padding}
      height={props.style.barIndicator.containerHeight}
      data-testid="bar-indicator"
    >
      <ProgressBarContainer position={props.style.barIndicator.position}>
        {progressIndicatorText && (
          <ProgressBarTotalContentText>
            {progressIndicatorText}
          </ProgressBarTotalContentText>
        )}
        {!props.unlimitedProducts && props.maxItems && props.maxItems > 1 ? (
          <StyledProgressBar
            height={props.style.barIndicator.barHeight}
            backgroundColor={props.style.barIndicator.backgroundColor}
          >
            <Bar
              completedRatio={
                (maxProgressBarValue / props.maxItems) * props.currentIndicator
              }
            />
          </StyledProgressBar>
        ) : (
          <BundlePrice>{props.totalPrice}</BundlePrice>
        )}
      </ProgressBarContainer>
      {displayAddToBasket && (
        <AddToBasketButton
          onClick={props.handleAddToBasketCallback}
          disabled={props.disabledAddToBasket}
        >
          {props.i18nText?.addToBagText}
        </AddToBasketButton>
      )}
    </BarWrapper>
  )
}

const CircularIndicator = (props: ProgressIndicatorProps) => {
  const maxCircularProgress: number =
    props.style.circularIndicator.shape === CircularIndicatorType.FULL
      ? 360
      : 265

  const displayAddToBasket = shouldDisplayAddToBasket(
    props.handleAddToBasketCallback,
    props.currentIndicator,
    props.maxItems,
    props.unlimitedProducts,
  )

  return (
    <CircularWrapper
      border={props.style.circularIndicator.border}
      data-testid="circular-indicator"
    >
      <CircularProgressContainer
        width={props.style.circularIndicator.width}
        height={props.style.circularIndicator.height}
        padding={props.style.circularIndicator.padding}
      >
        <StyledCircularProgress
          shape={props.style.circularIndicator.shape}
          progress={
            (maxCircularProgress / (props.maxItems || MAX_QUANTITY_SELECTION)) *
            props.currentIndicator
          }
        />
        {props.style.circularIndicator.shape === CircularIndicatorType.SEMI && (
          <StyledHiddenCircularProgress />
        )}
        <CircularTextWrapper shape={props.style.circularIndicator.shape}>
          <CircularCurrentIndicator
            textStyle={props.style.currentCountText.font}
          >
            {props.currentIndicator}
          </CircularCurrentIndicator>
          <CircularItemsText
            textStyle={props.style.itemsText.font}
            shape={props.style.circularIndicator.shape}
          >
            {props.unlimitedProducts || !props.maxItems
              ? props.i18nText?.itemsAlternateText
              : props.i18nText?.itemsText}
          </CircularItemsText>
        </CircularTextWrapper>
      </CircularProgressContainer>
      {displayAddToBasket && (
        <React.Fragment>
          <BundlePriceWrapper>
            <BundlePriceText>{props.i18nText?.totalText}</BundlePriceText>
            {props.bundleSummaryAmount ? (
              <StyledTotalSummary content={props.bundleSummaryAmount} />
            ) : (
              <BundlePrice>{props.totalPrice}</BundlePrice>
            )}
          </BundlePriceWrapper>
          <ButtonContainer>
            <AddToBasketButton
              disabled={props.disabledAddToBasket}
              onClick={() => props.handleAddToBasketCallback?.()}
              isCircularBar={true}
            >
              {props.i18nText?.addToBagText}
            </AddToBasketButton>
          </ButtonContainer>
        </React.Fragment>
      )}
    </CircularWrapper>
  )
}

export const ProgressIndicator = (props: ProgressIndicatorProps) => {
  switch (props.type) {
    case ProgressIndicatorType.CIRCULAR:
      return <CircularIndicator {...props} />

    case ProgressIndicatorType.BAR:
      return <BarIndicator {...props} />

    default:
      return null
  }
}
